import React from 'react';
import _, { isNaN, isFinite } from 'lodash';
import SmallDonutChartDetail from 'spw-components/charts/small-donut-chart-detail';

import {
  IProjectSpaceTypeCategory,
  IProjectSpaceTypeSubcategory,
} from 'spw-redux/modules/projects/types';
import {
  CUSTOM_SUBCATEGORY,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  PRIVATE_OFFICES_SUBCATEGORY,
  WORKSTATIONS_SUBCATEGORY,
} from 'spw-constants';
import { ISpacesBreakDown } from 'spw-redux/modules/program-summary/types';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { useSelector } from 'react-redux';
import {
  projectColors as selectProjectColors,
  selectCurrentProjectColors,
} from 'spw-redux/modules/projects/selectors';

interface ISpaceTypeAllocationWorkspaceUsfChart {
  category: IProjectSpaceTypeCategory;
  breakdown?: ISpacesBreakDown;
  style?: { [key: string]: number | string };
  chartStyle?: { [key: string]: number | string };
  showNamesInTable?: boolean;
}

const SpaceTypeAllocationWorkspaceUsfChart: React.FunctionComponent<ISpaceTypeAllocationWorkspaceUsfChart> = ({
  category,
  breakdown,
  style,
  chartStyle,
  showNamesInTable,
}) => {
  const { getUnit, convertArea } = useUnitConversion();
  const { individualWorkspaces: spaces } = breakdown;
  const projectColors = useSelector(selectProjectColors);
  const currentSubcategoryColors = useSelector(selectCurrentProjectColors)
    .subcategories;
  const categorySpaces = breakdown[category.defaultSpaceTypeCategory.category];
  const totalUsf = categorySpaces.reduce((usf, categorySpace) => {
    return usf + categorySpace.usf;
  }, 0);

  const data: (string | number)[][] = [['Space Type', 'Count']];

  const { subcategories } = category;
  const subcategoryColors: Record<
    string,
    { color: string; gradientColorIndex: number }
  > = {
    [WORKSTATIONS_SUBCATEGORY]:
      currentSubcategoryColors[WORKSTATIONS_SUBCATEGORY],
    [PRIVATE_OFFICES_SUBCATEGORY]:
      currentSubcategoryColors[PRIVATE_OFFICES_SUBCATEGORY],
    [CUSTOM_SUBCATEGORY]: currentSubcategoryColors[CUSTOM_SUBCATEGORY],
  };

  const subcategoriesData = subcategories.map(
    (subcategory: IProjectSpaceTypeSubcategory) => {
      const {
        category: defaultSubcategory,
      } = subcategory.defaultSpaceTypeCategory;
      const subcategorySpaces = spaces.filter(
        (space) => space.subcategory === defaultSubcategory
      );
      const spacesUsf = subcategorySpaces.reduce((sum, spaceTypeBreakdown) => {
        return sum + spaceTypeBreakdown.usf;
      }, 0);
      const subcategoryUsf = convertArea(spacesUsf);

      let percent = subcategoryUsf / totalUsf;

      if (isNaN(percent) || !isFinite(percent)) {
        percent = 0;
      }

      return {
        name: `All ${subcategory.name}`,
        quantity: subcategoryUsf,
        percent,
        color: subcategoryColors[defaultSubcategory].color,
      };
    }
  );

  subcategoriesData.forEach((subcategoryData) => {
    data.push([subcategoryData.name, subcategoryData.percent]);
  });

  const colors = [
    currentSubcategoryColors[WORKSTATIONS_SUBCATEGORY].color,
    currentSubcategoryColors[PRIVATE_OFFICES_SUBCATEGORY].color,
    currentSubcategoryColors[CUSTOM_SUBCATEGORY].color ||
      (projectColors[INDIVIDUAL_WORKSPACES_CATEGORY] as string),
  ];

  return (
    <div style={style} data-testid="space_type_allocation_workspace_usf_chart">
      <SmallDonutChartDetail
        isTotalProgramScenario={false}
        style={chartStyle}
        title="Space Type Allocation"
        subtitle={`${category.name} ${getUnit('area')}`}
        total={convertArea(totalUsf)}
        spacesData={subcategoriesData}
        data={data}
        colors={colors}
        showNamesInTable={showNamesInTable}
        showStackedNames
        testingPrefix=""
      />
    </div>
  );
};

export default SpaceTypeAllocationWorkspaceUsfChart;
